import _ from 'lodash';

import { User } from '../../models/Core/User';
import { Idea } from '../../models/Sessions/Idea/Idea';
import { Session } from '../../models/Sessions/Session';

export function generateUserIdToSessionsMapping(sessions, users) {
  const userIdToSessions = {};

  for (const user of users) {
    userIdToSessions[user._id] = sessions.filter(session => {
      const participantIds = session.config.participants.map(
        participant => participant['user_id'],
      );
      return participantIds.indexOf(user._id) >= 0;
    });
  }

  return userIdToSessions;
}

export function getSessionsForUsers(dataObject, users) {
  const allSessions = _.flatten(
    users.map(user => dataObject.userIdToSessions[user._id]),
  );

  return _.uniq(allSessions);
}

export function isValidIdea(idea) {
  return idea.published && !idea.deleted_at;
}

const sessionHasValidIdeas = session => {
  if (session.data && session.data.ideas) {
    return session.data.ideas.filter(isValidIdea).length > 0;
  }
  return false;
};

export function getValidSessions(sessions) {
  return sessions.filter(
    session =>
      !session.config.deactivated &&
      !session.config.restricted &&
      sessionHasValidIdeas(session),
  );
}

export function generateIdeaToSessionMapping(sessions) {
  const ideaIdToSession = {};

  for (const session of sessions) {
    for (const idea of getIdeasForSessions([session])) {
      ideaIdToSession[idea._id] = session;
    }
  }

  return ideaIdToSession;
}

export function findSessionsforIdeas(dataObject, selectedIdeas) {
  const allSessions = _.flatten(
    selectedIdeas.map(idea => dataObject.ideaIdToSession[idea._id]),
  );

  return _.uniq(allSessions);
  /*
  return allSessions.filter(session => {
    const ideaIds = session.data.ideas.map(idea => idea._id);
    for (const idea of selectedIdeas) {
      if (ideaIds.indexOf(idea._id) >= 0) {
        return true;
      }
    }

    return false;
  });*/
}

export function getFilteredSessionsByIds(dataObject, filteredSessionIds) {
  if (filteredSessionIds.length === 0) {
    return dataObject.sessions;
  } else {
    return getSessionsByIds(dataObject, filteredSessionIds);
  }
}

export function filterIdeasForTags(dataObject, filteredTags) {
  if (filteredTags.length === 0) {
    return getIdeasForSessions(dataObject.sessions);
  }

  return getIdeasForSessions(dataObject.sessions).filter(idea => {
    return ideaHasTags(idea, filteredTags);
  });
}

const getUsersById = (users, userIds) => {
  const mappedUsers = users.filter(user => {
    return userIds.indexOf(user._id.toString()) >= 0;
  });

  return mappedUsers;
};

export function getValidUsers(users) {
  return users.filter(
    user => user['verified'] && user['registered'] && user['access'] !== 1,
  );
}

const cachedCollaboratorsAndOriginatorsByIdeaId = {};

export function getCollaboratorsAndOriginatorsForIdeas(
  sessions,
  users,
  ideas: Idea[],
) {
  let userIds = [];
  for (const idea of ideas) {
    if (idea._id in cachedCollaboratorsAndOriginatorsByIdeaId) {
      userIds = userIds.concat(
        cachedCollaboratorsAndOriginatorsByIdeaId[idea._id],
      );
    } else {
      const userIdsForIdea = [];
      const session = getSessionForIdea(sessions, idea);
      const participantIdToUserId = mapParticipantIdToUserId(session);

      // let unsuccessfulMappings = 0;
      // let successfulMappings = 0;

      for (const collaborator of idea.collaborators) {
        const participantId = collaborator.participant_id.toString();

        if (participantId in participantIdToUserId) {
          userIdsForIdea.push(participantIdToUserId[participantId]);
          // successfulMappings++;
        } else {
          // unsuccessfulMappings++;
        }
      }

      for (const follower of idea.followers) {
        const participantId = follower.participant_id.toString();

        if (participantId in participantIdToUserId) {
          userIdsForIdea.push(participantIdToUserId[participantId]);
          // successfulMappings++;
        } else {
          // unsuccessfulMappings++;
        }
      }

      const creatorId = idea.created_by.toString();
      if (creatorId in participantIdToUserId) {
        userIdsForIdea.push(participantIdToUserId[creatorId]);
        // successfulMappings++;
      } else {
        // unsuccessfulMappings++;
      }

      cachedCollaboratorsAndOriginatorsByIdeaId[idea._id] = userIdsForIdea;
      userIds = userIds.concat(userIdsForIdea);
    }
  }

  return getUsersById(users, _.uniq(userIds));
}

const ideaHasTags = (idea, tags) => {
  const tagsForIdea = getTagsForIdeas([idea]);
  for (const tag of tags) {
    if (tagsForIdea.indexOf(tag) >= 0) {
      return true;
    }
  }

  return false;
};

export function getIdeasForUsers(dataObject, users, collaboratorsOnly = false) {
  if (collaboratorsOnly) {
    const filteredIdeas = _.flatten(
      users.map(user => getCollaboratedIdeasForUser(dataObject.sessions, user)),
    );

    return _.uniq(filteredIdeas);
  } else {
    const userSessions = getSessionsForUsers(dataObject, users);
    return getIdeasForSessions(userSessions);
  }
}

export function getSessionForIdea(sessions, idea) {
  for (const session of sessions) {
    if (
      session.data.ideas
        .map(ideaInSession => ideaInSession._id.toString())
        .indexOf(idea._id.toString()) >= 0
    ) {
      return session;
    }
  }

  return null;
}

export function getIdeasForSessions(sessions: unknown[]): Idea[] {
  return _.flatten(
    sessions.map(session => (session as any).data.ideas as Idea[]),
  ).filter(idea => idea.published && !idea.deleted_at);
}

export function getTagsForIdeas(ideas) {
  return _.flatten(ideas.map(idea => (idea['tags'] ? idea['tags'] : [])));
}

export function getUsersInCountries(dataObject, countries) {
  const users = dataObject.users;
  if (countries.length === 0) {
    return users;
  } else {
    return users.filter(user => {
      if (user && user.location && user.location.country) {
        return countries.indexOf(user.location.country) >= 0;
      }
      //user has no given location
      return false;
    });
  }
}

export function filterUsersForIds(dataObject, userIds) {
  if (userIds.length === 0) {
    return dataObject['users'];
  }

  return userIds
    .map(userId => dataObject['usersById'][userId])
    .filter(user => user);
}

export function generateUserByIdMapping(users) {
  const userById = {};
  for (const user of users) {
    userById[user._id] = user;
  }

  return userById;
}

export function generateSessionByIdMapping(sessions) {
  const sessionById = {};
  for (const session of sessions) {
    sessionById[session._id] = session;
  }

  return sessionById;
}

export function getUsersInSessions(dataObject, selectedSessionIds) {
  if (selectedSessionIds.length === 0) {
    return dataObject.users;
  }

  const sessions = getSessionsByIds(dataObject, selectedSessionIds);
  const userIds = _.uniq(
    _.flatten(
      sessions.map(session =>
        session.config.participants.map(participant => participant['user_id']),
      ),
    ),
  );

  return dataObject.users.filter(
    user => userIds.indexOf(user._id.toString()) >= 0,
  );
}

// returns users that are collaborator/originator for session that includes tags
export function filterUsersByTag(dataObject, tags) {
  if (tags.length === 0) {
    return dataObject.users;
  }

  return dataObject.users.filter(user => {
    const ideasForUser = getIdeasForUsers(dataObject.sessions, [user], true);

    const ideaTags = _.uniq(getTagsForIdeas(ideasForUser));
    for (const tag of tags) {
      if (ideaTags.indexOf(tag) >= 0) {
        return true;
      }
    }
    return false;
  });
}

export function getSessionsByIds(dataObject, sessionIds) {
  return sessionIds
    .map(sessionId => dataObject.sessionsById[sessionId])
    .filter(session => session);
}

const cachedCollaboratedIdeasWithSessionByUser = {};

const mapParticipantIdToUserId = session => {
  const participantIdToUserId = {};
  for (const participant of session.config.participants) {
    if (participant['user_id']) {
      participantIdToUserId[participant._id.toString()] =
        participant['user_id'].toString();
    }
  }

  return participantIdToUserId;
};

export function getCollaboratedIdeasForUser(
  sessions: Session[],
  user: User,
): Idea[] {
  if (!user) {
    return [];
  }

  if (user._id in cachedCollaboratedIdeasWithSessionByUser) {
    return cachedCollaboratedIdeasWithSessionByUser[user._id];
  }

  const allIdeas = [];
  for (const session of sessions) {
    const participantIdToUserId = mapParticipantIdToUserId(session);

    for (const idea of session.data.ideas) {
      if (!(idea.published && !idea.deleted_at)) {
        continue;
      }

      const userIsCollaborator = idea.collaborators.some(collaborator => {
        const participantId = collaborator.participant_id.toString();
        if (!participantId) {
          return false;
        }
        if (participantId in participantIdToUserId) {
          if (participantIdToUserId[participantId] === user._id.toString()) {
            return true;
          }
        }

        return false;
      });

      if (userIsCollaborator) {
        allIdeas.push(idea);
      } else {
        const creator_id = idea.created_by.toString();
        if (
          creator_id in participantIdToUserId &&
          participantIdToUserId[creator_id] === user._id.toString()
        ) {
          allIdeas.push(idea);
        }
      }
    }
  }

  cachedCollaboratedIdeasWithSessionByUser[user._id] = allIdeas;

  return allIdeas;
}
